/**
 * Sidebar component
 *
 * Main sidebar with logo, new chat button, session list, and user section.
 */

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, useSessions } from '../../hooks';
import { SessionList } from './SessionList';
import { UserSection } from './UserSection';
import { LanguageSelector } from './LanguageSelector';

interface SidebarProps {
  onClose?: () => void;
}

export function Sidebar({ onClose }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, login, logout } = useAuth();
  const {
    sessions,
    currentSessionUuid,
    selectSession,
    newSession,
    deleteSession,
  } = useSessions();

  const handleNewChat = async () => {
    try {
      const uuid = await newSession();
      // Navigate to chat page with session UUID
      navigate(`/chat/${uuid}`);
      // Close sidebar on mobile after creating new session
      onClose?.();
    } catch (error) {
      console.error('Failed to create new session:', error);
    }
  };

  const handleSelectSession = (uuid: string) => {
    selectSession(uuid);
    // Navigate to chat page with session UUID
    navigate(`/chat/${uuid}`);
    // Close sidebar on mobile after selecting session
    onClose?.();
  };

  // Hide sidebar completely for non-authenticated users (landing page takes full width)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="sidebar" data-testid="sidebar">
      <div className="sidebar-header">
        <div className="logo">Lucy</div>
        <button
          className="new-chat-btn"
          onClick={handleNewChat}
          data-testid="new-chat-btn"
        >
          {t('sidebar.newSession')}
        </button>
      </div>

      <SessionList
        sessions={sessions}
        currentSessionUuid={currentSessionUuid}
        onSelectSession={handleSelectSession}
        onDeleteSession={deleteSession}
      />

      <nav className="sidebar-nav">
        <Link
          to="/timeline"
          className={`nav-link ${location.pathname === '/timeline' ? 'active' : ''}`}
          onClick={onClose}
        >
          <span className="nav-icon">&#9734;</span>
          {t('timeline.title')}
        </Link>
        {/* Admin link - only visible to admins */}
        {user?.is_admin && (
          <Link
            to="/admin"
            className={`nav-link nav-link-admin ${location.pathname === '/admin' ? 'active' : ''}`}
            onClick={onClose}
          >
            <span className="nav-icon">&#9881;</span>
            Admin
          </Link>
        )}
      </nav>

      <div className="sidebar-footer">
        <LanguageSelector />
        <UserSection
          user={user}
          isAuthenticated={isAuthenticated}
          onLogin={login}
          onLogout={logout}
        />
      </div>
    </div>
  );
}

export default Sidebar;
